import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';

/**
 * @param {Date|null} left
 * @param {Date|null} right
 * @returns {boolean}
 */
const equalDates = (left, right) => {
  const l = left ? left.getTime() : 0;
  const r = right ? right.getTime() : 0;
  return l === r;
};

/** @module */
class SettingsOptionsController extends Controller {
  @service('commands.freelancer-calendar-commands') freelancerCalendarCommands;
  @service('state-managers/application') applicationStateManager;
  @service('commands/user-commands') userCommandService;
  @service store;
  @service moment;

  /**
   * @param {DS.Model} profile
   * @param {DS.Store} store
   * @param {{ startAt: Date, endAt: Date }|null} timeframe
   * @returns {Promise<void>}
   */
  updateCalendar (timeframe) {
    // get first eligible record
    const freelancerCalendar = this.model.profile.freelancerCalendars.find((calendar) => {
      return !calendar.isExpired;
    });

    if (!timeframe) {
      return this.freelancerCalendarCommands.delete(freelancerCalendar);
    }

    const startAt = this.moment.moment(timeframe.startAt);
    const endAt = this.moment.moment(timeframe.endAt);

    if (!freelancerCalendar) {
      // this is a new calendar entry
      return this.freelancerCalendarCommands.create(this.model.user, {
        startAt,
        endAt,
        status: 0,
      });
    }

    // records are equal, do nothing
    if (
      equalDates(freelancerCalendar.startAt, startAt)
      && equalDates(freelancerCalendar.endAt, endAt)
    ) {
      return Promise.resolve();
    }

    // persist the changes in the existing record
    return this.freelancerCalendarCommands.update(freelancerCalendar, {
      startAt,
      endAt,
    });
  };

  /**
   * @param {Object} opts
   * @param {boolean} opts.public_opt_out
   * @param {boolean} opts.ip_location_opt_out
   * @param {{ startAt: Date, endAt: Date }|null} opts.timeframe
   */
  @action
  updateUser({
    public_opt_out: publicOptOut,
    ip_location_opt_out: ipLocationOptOut,
    timeframe,
  }) {
    const calendarPromise = this.updateCalendar(timeframe);

    const profilePromise = this.userCommandService.updateUserProfile(this.model.user, this.model.profile, {
      publicOptOut,
      ipLocationOptOut,
    });

    return Promise.all([calendarPromise, profilePromise]).then(() => {
      this.applicationStateManager.addToast('Settings updated');
    });
  }
}

export default SettingsOptionsController;
